import { blue } from '@mui/material/colors';
import { alpha, SxProps, Theme } from '@mui/material/styles';

import { frostedPanelBg } from 'src/common/styles/frostedPanelStyles';

/** Default ticket card fill — matches participant registered cards. */
const TICKET_CARD_BG_OPACITY = 0.8;
const ticketCardBackground = (theme: Theme, opacity = TICKET_CARD_BG_OPACITY) =>
  frostedPanelBg(theme, opacity);

const TICKET_CARD_AVATAR_SIZE = 56;
const TICKET_CARD_AVATAR_STICK_OUT = 12;
const TICKET_CARD_AVATAR_TEXT_GAP = 8;
/** Inner content offset from card edge: mt 1.5 + pt 2 (12 + 16 px). */
const TICKET_CARD_INNER_CONTENT_TOP = 28;
const TICKET_CARD_INNER_INSET_X = 28;

interface TicketCardStyleOptions {
  inverted?: boolean;
  selected?: boolean;
  hasAvatar?: boolean;
  /** Opaque panel for image capture (no backdrop-filter). */
  solidPanel?: boolean;
}

export const ticketCardOuterSx = (
  theme: Theme,
  { inverted, selected, solidPanel }: TicketCardStyleOptions = {},
): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: 2,
  overflow: 'visible',
  ...(solidPanel
    ? {}
    : { backdropFilter: 'blur(3px)' }),
  ...(inverted
    ? {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.contrastText, 0.25),
      }
    : {
        bgcolor: solidPanel ? theme.palette.background.paper : ticketCardBackground(theme),
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
      }),
  ...(selected && !inverted
    ? {
        bgcolor: alpha(theme.palette.primary.main, 0.16),
        borderColor: 'primary.main',
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.24)}`,
      }
    : {
        boxShadow: 'none',
        ...(!selected && !inverted ? { opacity: 0.82 } : {}),
      }),
});

export const ticketCardInnerSx = (
  theme: Theme,
  { inverted, hasAvatar }: TicketCardStyleOptions = {},
): SxProps<Theme> => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  mx: 1.5,
  mb: 1.5,
  mt: hasAvatar ? 1 : 1.5,
  p: 2,
  pt: hasAvatar ? 1.25 : 2,
  border: '1px dashed',
  borderColor: inverted ? alpha(theme.palette.primary.contrastText, 0.45) : 'divider',
  borderRadius: 1,
  minHeight: 0,
});

/** Space beside avatar for the eyebrow row; title sits below on full width. */
const ticketCardAvatarColumnWidth =
  TICKET_CARD_AVATAR_SIZE -
  TICKET_CARD_AVATAR_STICK_OUT -
  TICKET_CARD_INNER_INSET_X +
  TICKET_CARD_AVATAR_TEXT_GAP;

/** Min height so title clears the overlapping avatar. */
const ticketCardAvatarClearance =
  TICKET_CARD_AVATAR_SIZE -
  TICKET_CARD_AVATAR_STICK_OUT -
  TICKET_CARD_INNER_CONTENT_TOP;

export const ticketCardAvatarShellSx: SxProps<Theme> = {
  position: 'relative',
  overflow: 'visible',
  height: '100%',
  mt: `${TICKET_CARD_AVATAR_STICK_OUT}px`,
};

/**
 * Standard dashboard ticket-card grid breakpoints (participants layout):
 * 1 / 2 / 3 / 4 columns at xs / sm / md / lg.
 */
export const ticketCardGridSize = { xs: 12, sm: 6, md: 4, lg: 3 } as const;

/** Horizontal padding on card grids inside overflow-hidden scroll regions (dashboard PageWrapper). */
export const ticketCardGridContainerPaddingSx: SxProps<Theme> = {
  pl: `${TICKET_CARD_AVATAR_STICK_OUT}px`,
  pr: `${TICKET_CARD_AVATAR_STICK_OUT}px`,
  pb: `${TICKET_CARD_AVATAR_STICK_OUT}px`,
};

/** Public scroll pages: horizontal avatar inset + bottom room for selection halo / footer. */
export const ticketCardPublicGridPaddingSx: SxProps<Theme> = {
  ...ticketCardGridContainerPaddingSx,
  pb: `${TICKET_CARD_AVATAR_STICK_OUT}px`,
};

/** Card grid body on dashboard pages (page shell owns scrolling). */
export const ticketCardScrollContainerSx = (_scrollable: boolean): SxProps<Theme> => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible',
  ...ticketCardGridContainerPaddingSx,
});

/** Flex shell for VirtualizedCardGrid under the sticky dashboard toolbar. */
export const ticketCardVirtualizedContainerSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'visible',
};

/** Use on Grid items that contain ticket cards with avatars. */
export const ticketCardGridItemSx: SxProps<Theme> = {
  overflow: 'visible',
  minWidth: 0,
  '& > *': {
    width: '100%',
    minWidth: 0,
  },
};

export const ticketCardAvatarTopRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: `${Math.max(ticketCardAvatarColumnWidth, 0)}px 1fr`,
  columnGap: 0.5,
  alignItems: 'end',
  minHeight: Math.max(ticketCardAvatarClearance, 0),
  mb: 0.75,
};

export const ticketCardStatusBorderSx = (color: string): SxProps<Theme> => ({
  borderLeftWidth: 8,
  borderLeftStyle: 'solid',
  borderLeftColor: color,
});

export const ticketCardAvatarWrapperSx = (
  theme: Theme,
  inverted?: boolean,
  solidPanel?: boolean,
): SxProps<Theme> => ({
  position: 'absolute',
  top: -TICKET_CARD_AVATAR_STICK_OUT,
  left: -TICKET_CARD_AVATAR_STICK_OUT,
  zIndex: 4,
  width: TICKET_CARD_AVATAR_SIZE,
  height: TICKET_CARD_AVATAR_SIZE,
  borderRadius: '50%',
  border: '1px dashed',
  borderColor: inverted ? alpha(theme.palette.primary.contrastText, 0.55) : 'divider',
  p: '3px',
  bgcolor: inverted ? 'primary.main' : solidPanel ? theme.palette.background.paper : ticketCardBackground(theme),
  ...(solidPanel ? {} : { backdropFilter: 'blur(3px)' }),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
  '& > .MuiAvatar-root': {
    width: '100% !important',
    height: '100% !important',
    fontSize: '1.1rem',
  },
  '& > .MuiBadge-root': {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiAvatar-root': {
      width: '100% !important',
      height: '100% !important',
    },
  },
  '& > .MuiTooltip-root': {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiIconButton-root': {
    width: '100% !important',
    height: '100% !important',
    minWidth: 0,
  },
  '& > .MuiSvgIcon-root': {
    width: '1.75rem',
    height: '1.75rem',
  },
});

export const ticketCardActionsSx = (inverted?: boolean): SxProps<Theme> => ({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 5,
  display: 'flex',
  gap: 0.25,
  ...(inverted
    ? {
        color: 'primary.contrastText',
        '& .MuiIconButton-root': { color: 'inherit' },
      }
    : {}),
});

export const ticketCardBadgeSx = (
  theme: Theme,
  highlighted: boolean,
  inverted?: boolean,
  accent: 'clay' | 'blue' | 'neutral' = 'clay',
): SxProps<Theme> => {
  const accentColors = {
    clay: {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
    },
    blue: {
      bgcolor: blue[700],
      color: '#fff',
    },
    neutral: {
      bgcolor: alpha(theme.palette.secondary.main, 0.14),
      color: 'secondary.main',
      border: '1px solid',
      borderColor: 'secondary.main',
    },
  } as const;

  return {
    height: 24,
    fontSize: '0.68rem',
    fontFamily: theme.typography.label.fontFamily,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    borderRadius: 999,
    ...(highlighted
      ? accentColors[accent]
      : inverted
        ? {
            bgcolor: alpha(theme.palette.primary.contrastText, 0.16),
            color: 'primary.contrastText',
            border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.35)}`,
          }
        : {
            bgcolor: alpha('#ffffff', 0.65),
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
          }),
  };
};

export const ticketCardBadgesRowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.75,
  mt: 'auto',
  pt: 2,
};

export const ticketCardFooterSx = (inverted?: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1.5,
  pb: 1.5,
  pt: 0.5,
  ...(inverted
    ? {
        color: 'primary.contrastText',
        '& .MuiIconButton-root': {
          color: 'inherit',
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
      }
    : {}),
});

export const ticketCardMediaSx: SxProps<Theme> = {
  mx: 1.5,
  mt: 1.5,
  borderRadius: 1,
  overflow: 'hidden',
  border: '1px dashed',
  borderColor: 'divider',
};
